import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { Link } from "react-router";
import { formatDuration } from "../../utils";
import { ProductType, CartItemType, LeadTimePriceType } from "../../types/index";
import useWishList from "../wishlist/useWishList.ts";
import useCart from "../cart/useCart.ts";
import { useAppState } from "../../context";

interface CardBlockProps extends ProductType {
    onWishlist?: boolean;
    inCart?: boolean;
    onVideoRef?: (ref: HTMLVideoElement | null) => void;
    onVideoPlay?: () => void;
}

const CardBlock = (props: CardBlockProps) => {
    const {
        name,
        slug,
        price,
        preview,
        images = [],
        clipProperties = undefined,
        onWishlist = false,
        inCart = false,
        onVideoRef,
        onVideoPlay
    } = props;

    const [videoError, setVideoError] = useState<string | null>(null);
    const [sliderActive, setSliderActive] = useState<boolean>(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const { addToWishlist, removeFromWishlist } = useWishList();
    const { updateCartItem, findCartItem, loading: isCartLoading, removeFromCart } = useCart();
    const [leadTimePrices] = useAppState<LeadTimePriceType[]>('leadTimePrices', []);
    
    const defaultCartItem = {
        product: props.id,
        additionalPhotoCount: 0,
        leadTimePrice: leadTimePrices.find(p => p.price === 0)?.id || undefined,
        ...findCartItem(props),
    } as CartItemType;


    // Handle video events
    useEffect(() => {
        const video = videoRef.current;

        const onVideoPlayEvent = () => {
            setIsVideoPlaying(true);
            onVideoPlay?.();
        };

        const onVideoError = () => {
            console.error('Video error occurred');
            setVideoError('Failed to load video');
        };

        const onVideoPause = () => {
            setIsVideoPlaying(false);
        };

        const onVideoEnded = () => {
            setIsVideoPlaying(false);
        };

        if (video) {
            video.addEventListener('play', onVideoPlayEvent);
            video.addEventListener('error', onVideoError);
            video.addEventListener('pause', onVideoPause);
            video.addEventListener('ended', onVideoEnded);
        }

        return () => {
            if (video) {
                video.removeEventListener('play', onVideoPlayEvent);
                video.removeEventListener('error', onVideoError);
                video.removeEventListener('pause', onVideoPause);
                video.removeEventListener('ended', onVideoEnded);
            }
        }
    }, [onVideoPlay]);

    useEffect(() => {
        onVideoRef?.(videoRef.current);
        return () => onVideoRef?.(null);
    }, [onVideoRef]);

    const playVideo = () => {
        if (videoRef.current && !videoError) {
            videoRef.current.play().catch(error => {
                console.error('Failed to play video:', error);
                setVideoError('Failed to play video');
            });
        }
    };

    const pauseVideo = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
    };

    const toggleVideo = () => {
        if (isVideoPlaying) {
            pauseVideo();
        } else {
            playVideo();
            setSliderActive(false);
        }
    };

    const handleSliderToggle = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
        if (isVideoPlaying) {
            pauseVideo();
        }
        if (sliderActive) {
            ev.currentTarget.blur();
        }
        setSliderActive(prev => !prev);
    }, [isVideoPlaying]);

    const handleSliderClose = () => {
        if (isVideoPlaying) {
            pauseVideo();
        }
        setSliderActive(false);
    };

    const { imageCount, duration } = clipProperties || {};

   
    return (
        <div className={`prodcard ${sliderActive ? 'm-active-slider' : (isVideoPlaying ? ' m-active-video' : '')} `}>
            <div className="prodcard__cover">
                <Link to={`/catalog/product/${slug}`} className="prodcard__maincover">
                    {images.length > 0 && <picture className="prodcard__pic">
                        <source type="image/webp" srcSet={`/api/media/${images[0].image}?webp&s=s 1x, /api/media/${images[0].image}?webp&s=m 2x`} />
                        <img className="prodcard__pic-img"
                            srcSet={`/api/media/${images[0].image}?s=m 2x`}
                            src={`/api/media/${images[0].image}?s=s`}
                            alt={name} loading="lazy" />
                    </picture>}
                    <span className="prodcard__price">${price}</span>
                </Link>
                <div className="prodcard__slider-holder">
                    <Swiper className="prodcard__slider swiper js_swiper_prodcard"
                        initialSlide={1}
                        modules={[Navigation]}
                        loop={true}
                        navigation={{
                            nextEl: '.slider__controls .slider__arrow_sub-next',
                            prevEl: '.slider__controls .slider__arrow_sub-prev'
                        }}
                    >
                        {images.map(({ image }, index) => (
                            <SwiperSlide className="swiper-slide" key={index}>
                                <picture className="prodcard__fullpic">
                                    <source type="image/webp" srcSet={`/api/media/${image}?webp&s=s 1x, /api/media/${image}?webp&s=m 2x`} />
                                    <img className="prodcard__fullpic-img"
                                        src={`/api/media/${image}?s=s`}
                                        srcSet={`/api/media/${image}?s=m 2x`}
                                        alt="" loading="lazy" />
                                </picture>
                            </SwiperSlide>
                        ))}
                        <div className="slider__controls">
                            <div className="slider__arrow slider__arrow_sub-prev"></div>
                            <div className="slider__arrow slider__arrow_sub-next"></div>
                        </div>
                        <button
                            onClick={handleSliderClose}
                            className="btn-media-close prodcard__slider-close"
                            type="button"
                        >
                            <svg role="img" className="btn-media-close__ico">
                                <use href="./img/sprite.svg#cross"></use>
                            </svg>
                        </button>
                    </Swiper>
                </div>
                {preview && <div className={`prodcard__fullvideo video js_video ${isVideoPlaying ? ' m-active' : ''}`}>
                    <video className="video__video"
                        ref={videoRef}
                        preload="metadata"
                        playsInline
                        webkit-playsinline="true"
                        poster={`/api/media/${images[0].image}?s=l`}
                        controls={true}
                    >
                        <source src={`/api/media/${preview}?.mp4`} type="video/mp4" />
                    </video>
                    <button className="btn-play" onClick={playVideo} type="button" aria-label="Play"></button>
                    <button onClick={handleSliderClose}
                        className="btn-media-close prodcard__video-close"
                        type="button"
                    >
                        <svg role="img" className="btn-media-close__ico">
                            <use href="./img/sprite.svg#cross"></use>
                        </svg>
                    </button>
                </div>}
            </div>
            <div className="prodcard__body">
                <Link to={`/catalog/product/${slug}`} className="prodcard__title">
                    {name}
                </Link>

                {clipProperties && Object.keys(clipProperties).length > 0 && <div className="prodinfo">
                    <div className="prodinfo__item">
                        <svg role="img" className="prodinfo__ico">
                            <use href="./img/sprite.svg#pc_info_pics"></use>
                        </svg>
                        {imageCount} pic
                    </div>
                    <div className="prodinfo__item">
                        <svg role="img" className="prodinfo__ico">
                            <use href="./img/sprite.svg#pc_info_time"></use>
                        </svg>
                        {formatDuration(String(duration))}
                    </div>
                </div>}
                <div className="prodcard__btns">
                    {preview &&
                        <button onClick={toggleVideo} className="prodcard__btn btn btn_cta" type="button" title="Video">
                            <svg role="img" className="btn__ico">
                                <use href="./img/sprite.svg#pc_video"></use>
                            </svg>
                        </button>
                    }
                    {images.length > 0 && <button
                        onClick={handleSliderToggle}
                        className="prodcard__btn btn btn_cta"
                        type="button"
                        title="Pics"
                    >
                        <svg role="img" className="btn__ico">
                            <use href="./img/sprite.svg#pc_pics"></use>
                        </svg>
                    </button>}
                    <button
                        onClick={() => onWishlist ? removeFromWishlist(props) : addToWishlist(props)}
                        className={`prodcard__btn btn btn_wishlist${onWishlist ? '-active' : ''}`}
                        type="button"
                        title={`${onWishlist ? 'Remove from' : 'Add to'} Wishlist`}
                    >
                        <svg role="img" className="btn__ico">
                            <use href="./img/sprite.svg#pc_heart"></use>
                        </svg>
                    </button>

                    {!isCartLoading && (
                        <button
                            disabled={!props.isAvailable}
                            onClick={() => inCart ? removeFromCart(defaultCartItem) : updateCartItem(defaultCartItem)}
                            className={`prodcard__btn btn btn_cart${inCart ? ' btn_cart-active' : ''}`}
                            type="button"
                            title={`${inCart ? 'Remove from' : 'Add to'} Cart`}
                        >
                            <svg role="img" className="btn__ico">
                                <use href="./img/sprite.svg#pc_cart"></use>
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CardBlock;


